// E8OrigamiSheetsOpenTask.js
// Zadanie otwarte E8 (2026 zad. 15) - kartki do origami, rownanie z jedna niewiadoma.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData({ red, white, greenLess }) {
  const blue = (3 * red) / 2;
  const green = red - greenLess;
  const total = white + red + blue + green;
  return { red, blue, green, white, greenLess, total, maxPoints: 2 };
}

function createOriginalTask() {
  return {
    type: "E8OrigamiSheetsOpenTask",
    data: buildData({ red: 38, white: 37, greenLess: 10 }),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 15 },
    tags: ["e8", "algebra", "rownania", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const red = pick([24, 28, 30, 32, 36, 40, 44, 48]);
  const greenLess = pick([4, 6, 8, 10, 12]);
  const white = pick([25, 30, 32, 35, 37, 40, 45]);
  return {
    type: "E8OrigamiSheetsOpenTask",
    data: buildData({ red, white, greenLess }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_15" },
    tags: ["e8", "algebra", "rownania", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

function raw(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return "";
}

function normalize(text) {
  return String(text || "").toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function parseNumber(value) {
  const match = normalize(raw(value)).match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function setupLooksCorrect(text, data) {
  const t = normalize(text);
  return (
    t.includes(String(data.total)) &&
    t.includes(String(data.white)) &&
    (t.includes("1.5") || t.includes("3/2")) &&
    t.includes(String(data.greenLess)) &&
    t.includes("x")
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Do wykonania origami Ela przygotowała łącznie <InlineMath math={`${data.total}`} /> kartek. Każda z tych kartek była w jednym z czterech kolorów: białym, niebieskim, zielonym lub czerwonym. Kartek białych było <InlineMath math={`${data.white}`} />. Kartek niebieskich było <InlineMath math="1{,}5" /> raza więcej niż czerwonych, a kartek zielonych było o <InlineMath math={`${data.greenLess}`} /> mniej niż czerwonych.
      </p>
      <p>Oblicz, ile kartek niebieskich przygotowała Ela. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.45, borderLeft: "3px solid var(--color-accent)", paddingLeft: 8 }}>
        <strong>{"Obliczenia s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz pokaza\u0107 tok rozwi\u0105zania. Poprawna liczba kartek niebieskich daje pe\u0142ne 2 punkty."}
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Równanie lub obliczenia:</div>
        <EquationInput
          value={ua.setup || ""}
          onChange={(val) => onChange?.({ ...ua, setup: val })}
          disabled={disabled}
          placeholder="np. x + 1,5x + (x - 10) + 37 = 160"
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Liczba kartek niebieskich:</div>
        <FractionInput
          value={ua.result || ""}
          onChange={(val) => onChange?.({ ...ua, result: val })}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{kartek}"
          placeholder="np. 57"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const resultOk = parseNumber(userAnswer.result) === data.blue;
  const setupOk = setupLooksCorrect(userAnswer.setup, data);
  return scoreStaged({
    resultOk,
    milestones: [setupOk],
    maxScore: data.maxPoints,
    parts: { "równanie / zależności": setupOk, "wynik": resultOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`x+1{,}5x+(x-${data.greenLess})+${data.white}=${data.total}`} />
      <BlockMath math={`x=${data.red}`} />
      <BlockMath math={`1{,}5\\cdot ${data.red}=${data.blue}`} />
      <div>{"Odpowied\u017a: Ela przygotowa\u0142a "}<InlineMath math={`${data.blue}`} />{" kartek niebieskich."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const setup = raw(userAnswer.setup);
  const result = raw(userAnswer.result);
  if (!setup && !result) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"R\u00f3wnanie / obliczenia: "}{setup || <i>(brak)</i>}</div>
      <div>{"Liczba kartek niebieskich: "}<InlineMath math={`${result || "--"}\\ \\text{kartek}`} /></div>
    </div>
  );
}
export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Oznaczamy przez <InlineMath math="x" /> liczbę kartek czerwonych.</p>
      <BlockMath math={`x+1{,}5x+(x-${data.greenLess})+${data.white}=${data.total}`} />
      <p><strong>Krok 2.</strong> Rozwiązujemy równanie.</p>
      <BlockMath math={`3{,}5x=${data.total - data.white + data.greenLess}`} />
      <BlockMath math={`x=${data.red}`} />
      <p><strong>Krok 3.</strong> Kartek niebieskich było <InlineMath math="1{,}5" /> raza więcej niż czerwonych.</p>
      <BlockMath math={`1{,}5\\cdot ${data.red}=${data.blue}`} />
      <p>Ela przygotowała <InlineMath math={`${data.blue}`} /> kartek niebieskich.</p>
    </div>
  );
}
